import React, { useState, useCallback, useEffect } from 'react';
import { Table, message, Avatar } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { getCredentialResources } from '../CredentialsServices';

const ResourcesModal = ({ credential, onCancel }) => {
    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const fetchResources = useCallback(async (page = 1, pageSize = 10) => {
        if (!credential?.id) return;

        setLoading(true);
        try {
            const response = await getCredentialResources({
                credential_id: credential.id,
                page,
                size: pageSize,
                sort_by: 'name',
                sort_direction: 'asc',
            });

            setResources(response.data || []);
            setPagination(prev => ({
                ...prev,
                current: response.metadata?.page || page,
                pageSize: response.metadata?.page_size || pageSize,
                total: response.metadata?.total_count || 0,
            }));
        } catch (error) {
            handleError(error);
            setResources([]);
        } finally {
            setLoading(false);
        }
    }, [credential?.id, handleError]);

    useEffect(() => {
        if (credential?.id) {
            fetchResources(1, 10);
        }
    }, [credential?.id, fetchResources]);

    const handleTableChange = (newPagination) => {
        fetchResources(newPagination.current, newPagination.pageSize);
    };

    const columns = [
        {
            title: '',
            key: 'picture',
            width: 60,
            render: (_, record) => (
                <Avatar
                    src={record.picture_url}
                    icon={<UserOutlined />}
                />
            ),
        },
        {
            title: t('id'),
            dataIndex: 'id',
            key: 'id',
            width: 100,
        },
        {
            title: t('name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('category'),
            dataIndex: 'category',
            key: 'category',
        },
        {
            title: t('about'),
            dataIndex: 'about',
            key: 'about',
            ellipsis: true,
        },
    ];

    if (!credential) return null;

    return (
        <ResponsiveModal
            title={t('credential_resources')}
            open
            onCancel={onCancel}
            desktopWidth={800}
            footer={null}
        >
            <Table
                columns={columns}
                dataSource={resources}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ x: 400 }}
            />
        </ResponsiveModal>
    );
};

export default ResourcesModal;
